import type { FaceLandmarker as FaceLandmarkerType, FaceLandmarkerOptions } from "@mediapipe/tasks-vision";
import { toSample, type FaceDetector } from "./faceAlignment";

// The MediaPipe Face Landmarker behind the recorder's alignment hints. Its module and wasm are served from
// public/mediapipe/runtime (copied by scripts/copy-mediapipe-wasm.js) and imported at run time, not bundled;
// the model beside them is committed. Types still come from the package.

type Vision = typeof import("@mediapipe/tasks-vision");

const BASE = `${process.env.PUBLIC_URL || ""}/mediapipe`;
const RUNTIME = `${BASE}/runtime`;

export async function loadFaceDetector(): Promise<FaceDetector> {
    const { FilesetResolver, FaceLandmarker } = (await import(/* webpackIgnore: true */ `${RUNTIME}/vision_bundle.mjs`)) as Vision;
    const vision = await FilesetResolver.forVisionTasks(RUNTIME);
    const options = (delegate: "GPU" | "CPU"): FaceLandmarkerOptions => ({
        baseOptions: { modelAssetPath: `${BASE}/face_landmarker.task`, delegate },
        runningMode: "VIDEO" as const,
        numFaces: 1,
    });
    let landmarker: FaceLandmarkerType;
    try {
        landmarker = await FaceLandmarker.createFromOptions(vision, options("GPU"));
    } catch {
        landmarker = await FaceLandmarker.createFromOptions(vision, options("CPU"));
    }
    // The landmarker wants a strictly increasing clock.
    let last = 0;
    return {
        detect(video, timestampMs) {
            const ts = timestampMs <= last ? last + 1 : timestampMs;
            last = ts;
            return toSample(landmarker.detectForVideo(video, ts).faceLandmarks[0]);
        },
        close() {
            landmarker.close();
        },
    };
}
